import { useCallback, useEffect, useMemo } from 'react';

import { meshModules } from 'Deer/constants/modules';
import { L3_PATHS } from 'Deer/constants/routes';
import { getUserAccess } from 'Deer/helpers/getUserAccess';
import { useRouteToMyTeamLevels, useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import { IntentFilters } from 'Deer/intent/components/Layout/IntentFilters.layout';
import { IntentBreadcrumbType } from 'Deer/intent/types/IntentCommonTypes';
import { generateCSVContinousFeedbackDataApi } from 'api/analytics/feedback';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';
import { useLevelUpAnalytics } from 'screens/analytics/continuousFeedback/context';
import { checkDenyPublicCFVisibilityEnabled, checkVisierAnalyticsEnabled } from 'utils/helpers/featureFlags';
import { checkFeedbackModuleEnabled } from 'utils/helpers/moduleAccess';
import { checkUserIsFeedbackAdmin } from 'utils/helpers/rolesAccess';

import { Download } from 'assets/assetsV1/iconComponents/Download';

import { localStorageKeys } from 'constants/localStorageKeys';
import { sendDownloadCSVEvent } from 'constants/mixpanelHelper';
import { FEEDBACK_INTENTS } from 'constants/moduleIntentTypes';

import { getTextFromTextMacros } from 'services/helpers/post';

import { AgGridEllipsisMenu } from 'uiToolkitV1/AgGridWrapperV2/components/EllipsisMenu/AgGridEllipsisMenu';

import { useCFCSVDownload } from '../hooks/useCFCSVDownload';
import {
  CompetenciesIntentType,
  setSelectedCompetenciesIntent,
  useSelectCompetenciesSelectedIntent,
} from '../slice/CompetenciesIntentSlice';
import { CompetenciesMetaFilters } from './CompetenciesMetaFilters';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CompetenciesIntentDropdown = ({
  showKebabMenu = true,
  breadcrumbs,
  gridRef,
}: {
  showKebabMenu?: boolean;
  breadcrumbs: IntentBreadcrumbType;
  gridRef?: unknown;
}) => {
  const { l1Module } = useRouteToNavLevels();
  const { teamType } = useRouteToMyTeamLevels();
  const history = useHistory();
  const { pathname, search } = useLocation();
  const dispatch = useDispatch();
  const sideNavSelectedCohort = useSelectedView();
  const userAccess = getUserAccess();

  const feedbackViewFromQueryParams = new URLSearchParams(search).get('feedbackView');

  const { state } = useLevelUpAnalytics() ?? {};
  const { currentState } = state ?? {};

  const { selectedGroupByKey, selectedSource, selectedFilter, selectedDateRange } = currentState;

  // eslint-disable-next-line consistent-return
  const generateCSVData = useCallback(() => {
    sendDownloadCSVEvent({
      eventProperties: {
        cohortId: sideNavSelectedCohort?.id,
        cohortName: sideNavSelectedCohort?.name,
        source: 'Feedback',
      },
    });
    try {
      // @ts-expect-error legacy code
      return generateCSVContinousFeedbackDataApi({
        cohort: sideNavSelectedCohort,
        groupByKeys: selectedGroupByKey,
        source: selectedSource,
        filter: selectedFilter,
        dateRange: selectedDateRange,
      });
    } catch (error) {
      console.error('Error generating CSV data for feedback', error);
    }
  }, [sideNavSelectedCohort, selectedGroupByKey, selectedSource, selectedFilter, selectedDateRange]);

  const { selectedIntent: selectedCFIntent } = useSelectCompetenciesSelectedIntent() ?? {};
  const { showDownloadCSV } = selectedCFIntent ?? {};

  const { generateFile } = useCFCSVDownload({
    downloadFileHandler: generateCSVData,
    localStorageKey: localStorageKeys.reviewTablesCSVDownloads,
    fileName: sideNavSelectedCohort?.type,
  });

  const downloadTeamFeedbackCSV = useCallback(() => {
    gridRef?.current?.api.exportDataAsCsv({
      fileName: `team_feedback_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.csv`,
      allColumns: true,
      columnKeys: ['employee', 'sharedFor', 'feedbackType', 'date', 'comment'], // ignoring kebab menu column
      processCellCallback: (params) => {
        const colId = params?.column?.colDef?.colId;
        if (!colId) return params.value;

        try {
          switch (colId) {
            case 'comment':
              return getTextFromTextMacros(
                params?.node?.data?.comment?.value?.text,
                params?.node?.data?.comment?.value?.macros
              );
            case 'date':
              return format(new Date(params.value), 'PP');
            default:
              return params.value;
          }
        } catch {
          return params.value;
        }
      },
    });
  }, [gridRef]);

  // eslint-disable-next-line arrow-body-style
  const ellipsisMenuOptions = useMemo(() => {
    const options = [];

    if (showDownloadCSV) {
      options.push({
        label: 'Download CSV',
        onClick: selectedCFIntent?.id === FEEDBACK_INTENTS.TEAM_FEEDBACK ? downloadTeamFeedbackCSV : generateFile,
        Icon: Download,
      });
    }

    return options;
  }, [showDownloadCSV, selectedCFIntent?.id, downloadTeamFeedbackCSV, generateFile]);

  // eslint-disable-next-line arrow-body-style
  const intentDropdownOptions = useMemo<Array<CompetenciesIntentType>>(() => {
    return [
      {
        id: FEEDBACK_INTENTS.COMPETENCIES,
        label: `Competencies`,
        onClick: () => {
          const values = {
            myData: L3_PATHS['my-data'].develop.competencies,
            team: {
              directReports: L3_PATHS.team.directReports.develop.manage,
            },
          };
          const isTeamRoute = l1Module === 'team';
          const urlToPush = isTeamRoute ? values.team[teamType] : (values[l1Module] ?? values.myData);

          history.push({
            pathname: urlToPush,
          });
        },
        visible: () => l1Module === 'myData' || (l1Module === 'team' && teamType === 'directReports'),
        value: {
          myData: {
            path: L3_PATHS['my-data'].develop.competencies,
            isAccessible: checkFeedbackModuleEnabled,
          },
          team: {
            directReports: {
              path: L3_PATHS.team.directReports.develop.manage,
              isAccessible: checkFeedbackModuleEnabled,
            },
          },
        },
        showTimelineDropdown: false,
        category: 'Manage',
        isDefault: true,
      },
      {
        id: FEEDBACK_INTENTS.TEAM_FEEDBACK,
        label: 'Team feedback',
        onClick: () => {
          history.push({
            pathname: L3_PATHS.team.directReports.develop.feedbackForMyTeam,
          });
        },
        visible: () => l1Module === 'team' && teamType === 'directReports',
        value: {
          team: {
            directReports: {
              path: L3_PATHS.team.directReports.develop.feedbackForMyTeam,
              isAccessible: checkFeedbackModuleEnabled,
            },
          },
        },
        showTimelineDropdown: false,
        category: 'Manage',
        isDefault: false,
        showDownloadCSV: true,
      },
      {
        id: FEEDBACK_INTENTS.COMPANY_PRAISES,
        label: 'Company praises',
        onClick: () => {
          history.push({
            pathname: L3_PATHS['my-data'].develop.organisationPraise,
          });
        },
        visible: () => l1Module === 'myData' && !checkDenyPublicCFVisibilityEnabled(),
        value: {
          myData: {
            path: L3_PATHS['my-data'].develop.organisationPraise,
            isAccessible: checkFeedbackModuleEnabled,
          },
        },
        showTimelineDropdown: false,
        category: 'Manage',
        isDefault: false,
      },
      {
        id: FEEDBACK_INTENTS.FEEDBACK_PENDING_ON_ME,
        label: 'Pending on me',
        onClick: () => {
          const urlToPush = L3_PATHS['my-data'].develop.feedback;

          const queryParams = new URLSearchParams(search);
          queryParams.set('feedbackView', 'PENDING');

          history.push({
            pathname: urlToPush,
            search: queryParams.toString(),
          });
        },
        visible: () => checkFeedbackModuleEnabled() && l1Module === 'myData',
        value: {
          myData: {
            path: L3_PATHS['my-data'].develop.feedback,
            isAccessible: checkFeedbackModuleEnabled,
          },
        },
        queryParamValue: 'PENDING',
        showTimelineDropdown: false,
        category: 'Manage',
        isDefault: true,
      },
      {
        id: FEEDBACK_INTENTS.FEEDBACK_RECEIVED_BY_ME,
        label: 'Received by me',
        onClick: () => {
          const urlToPush = L3_PATHS['my-data'].develop.feedback;

          const queryParams = new URLSearchParams(search);
          queryParams.set('feedbackView', 'RECEIVED');

          history.push({
            pathname: urlToPush,
            search: queryParams.toString(),
          });
        },
        visible: () => checkFeedbackModuleEnabled() && l1Module === 'myData',
        value: {
          myData: {
            path: L3_PATHS['my-data'].develop.feedback,
            isAccessible: checkFeedbackModuleEnabled,
          },
        },
        queryParamValue: 'RECEIVED',
        showTimelineDropdown: false,
        category: 'Manage',
        isDefault: true,
      },
      {
        id: FEEDBACK_INTENTS.FEEDBACK_SHARED_BY_ME,
        label: 'Shared by me',
        onClick: () => {
          const urlToPush = L3_PATHS['my-data'].develop.feedback;

          const queryParams = new URLSearchParams(search);
          queryParams.set('feedbackView', 'SHARED');

          history.push({
            pathname: urlToPush,
            search: queryParams.toString(),
          });
        },
        visible: () => checkFeedbackModuleEnabled() && l1Module === 'myData',
        value: {
          myData: {
            path: L3_PATHS['my-data'].develop.feedback,
            isAccessible: checkFeedbackModuleEnabled,
          },
        },
        queryParamValue: 'SHARED',
        showTimelineDropdown: false,
        category: 'Manage',
        isDefault: true,
      },
      {
        id: FEEDBACK_INTENTS.FEEDBACK_REQUESTS_FROM_ME,
        label: 'Requests from me',
        onClick: () => {
          const urlToPush = L3_PATHS['my-data'].develop.feedback;

          const queryParams = new URLSearchParams(search);
          queryParams.set('feedbackView', 'REQUESTED');

          history.push({
            pathname: urlToPush,
            search: queryParams.toString(),
          });
        },
        visible: () => checkFeedbackModuleEnabled() && l1Module === 'myData',
        value: {
          myData: {
            path: L3_PATHS['my-data'].develop.feedback,
            isAccessible: checkFeedbackModuleEnabled,
          },
        },
        queryParamValue: 'REQUESTED',
        showTimelineDropdown: false,
        category: 'Manage',
        isDefault: true,
      },
      {
        id: FEEDBACK_INTENTS.FEEDBACK_TABLE_REPORTS,
        label: 'Table reports',
        onClick: () => {
          const values = {
            team: {
              directReports: L3_PATHS.team.directReports.develop.insights,
              allReports: L3_PATHS.team.allReports.develop.insights,
            },
            company: L3_PATHS.company.develop.insights,
          };

          const isTeamRoute = l1Module === 'team';
          const urlToPush = isTeamRoute ? values.team[teamType] : (values[l1Module] ?? values.team.directReports);

          history.push({
            pathname: urlToPush,
          });
        },
        visible: () =>
          checkFeedbackModuleEnabled() &&
          (l1Module === 'team' ||
            (l1Module === 'company' && (checkUserIsFeedbackAdmin() || userAccess?.isCustomFeedbackAdmin))),
        value: {
          team: {
            directReports: {
              path: L3_PATHS.team.directReports.develop.insights,
              isAccessible: checkFeedbackModuleEnabled,
            },
            allReports: {
              path: L3_PATHS.team.allReports.develop.insights,
              isAccessible: checkFeedbackModuleEnabled,
            },
          },
          company: {
            path: L3_PATHS.company.develop.insights,
            isAccessible: () =>
              checkFeedbackModuleEnabled() && (checkUserIsFeedbackAdmin() || userAccess?.isCustomFeedbackAdmin),
          },
        },
        showTimelineDropdown: true,
        category: 'Analyze',
        isDefault: true,
        showGroupByDropdown: true,
        showDownloadCSV: true,
      },
      {
        id: FEEDBACK_INTENTS.FEEDBACK_GRAPH_REPORTS,
        label: 'Graph reports',
        onClick: () => {
          const values = {
            team: {
              directReports: L3_PATHS.team.directReports.develop.embeddedAnalytics,
              allReports: L3_PATHS.team.allReports.develop.embeddedAnalytics,
            },
            company: L3_PATHS.company.develop.embeddedAnalytics,
          };

          const isTeamRoute = l1Module === 'team';
          const urlToPush = isTeamRoute ? values.team[teamType] : (values[l1Module] ?? values.team.directReports);

          history.push({
            pathname: urlToPush,
          });
        },
        visible: () =>
          checkFeedbackModuleEnabled() &&
          l1Module !== 'myData' &&
          checkVisierAnalyticsEnabled() &&
          checkUserIsFeedbackAdmin(),
        value: {
          team: {
            directReports: {
              path: L3_PATHS.team.directReports.develop.embeddedAnalytics,
              isAccessible: () => checkFeedbackModuleEnabled() && checkVisierAnalyticsEnabled(),
            },
            allReports: {
              path: L3_PATHS.team.allReports.develop.embeddedAnalytics,
              isAccessible: () => checkFeedbackModuleEnabled() && checkVisierAnalyticsEnabled(),
            },
          },
          company: {
            path: L3_PATHS.company.develop.embeddedAnalytics,
            isAccessible: () => checkFeedbackModuleEnabled() && checkVisierAnalyticsEnabled(),
          },
        },
        showTimelineDropdown: false,
        category: 'Analyze',
        isDefault: true,
      },
      {
        id: FEEDBACK_INTENTS.COMPETENCY_LIBRARY,
        label: 'Competency library',
        onClick: () => {
          history.push({
            pathname: L3_PATHS.company.develop.competencyLibrary,
          });
        },
        visible: () => checkFeedbackModuleEnabled() && checkUserIsFeedbackAdmin() && l1Module === 'company',
        value: {
          company: {
            path: L3_PATHS.company.develop.competencyLibrary,
            isAccessible: () => checkFeedbackModuleEnabled() && checkUserIsFeedbackAdmin(),
          },
        },
        showTimelineDropdown: false,
        category: 'Configure',
        isDefault: true,
      },
      {
        id: FEEDBACK_INTENTS.FEEDBACK_SETTINGS,
        label: 'Settings',
        onClick: () => {
          history.push({
            pathname: L3_PATHS.company.develop.competencySettings,
          });
        },
        visible: () => checkFeedbackModuleEnabled() && checkUserIsFeedbackAdmin() && l1Module === 'company',
        value: {
          company: {
            path: L3_PATHS.company.develop.competencySettings,
            isAccessible: () => checkFeedbackModuleEnabled() && checkUserIsFeedbackAdmin(),
          },
        },
        showTimelineDropdown: false,
        category: 'Configure',
        isDefault: true,
      },
    ];
  }, [l1Module, teamType, history, search, userAccess?.isCustomFeedbackAdmin]);

  const selectedIntent = useMemo(
    () =>
      intentDropdownOptions.find((option) => {
        // for query param based intent options such as review landing page, pending on me, pending on others etc
        if (option.queryParamValue) {
          return feedbackViewFromQueryParams === option.queryParamValue && pathname.includes(option.value.myData.path);
        }
        const isTeamType = l1Module === 'team';
        if (isTeamType) {
          return pathname.includes(option.value.team?.[teamType]?.path);
        }
        return pathname.includes(option.value[l1Module]?.path);
      }),
    [intentDropdownOptions, l1Module, pathname, feedbackViewFromQueryParams, teamType]
  );

  useEffect(() => {
    // if selectedIntent changes then update it in redux
    dispatch(setSelectedCompetenciesIntent(selectedIntent));
  }, [selectedIntent, dispatch]);

  const ellipsisMenuOptionsExist = ellipsisMenuOptions?.length > 0;

  return (
    <IntentFilters
      intentDropdownOptions={intentDropdownOptions.filter((option) => option.visible())}
      selectedIntent={selectedIntent?.label ?? ''}
      selectedIntentId={selectedIntent?.id ?? ''}
      KebabMenu={ellipsisMenuOptionsExist && <AgGridEllipsisMenu options={ellipsisMenuOptions} />}
      MetaFilters={<CompetenciesMetaFilters />}
      showKebabMenu={
        l1Module === 'team' && selectedIntent?.id !== FEEDBACK_INTENTS.TEAM_FEEDBACK ? false : showKebabMenu
      }
      moduleName={meshModules.develop.value}
      intentTitle={meshModules.develop.label}
      isCategorized
      breadcrumbs={breadcrumbs}
      ellipsisMenuOptions={ellipsisMenuOptions}
    />
  );
};

export { CompetenciesIntentDropdown };
